import React, { useMemo } from 'react';

import { MAX_IMAGE_SIZE } from '@lumapps/constants';
import { useImageLightboxProvider } from '@lumapps/lumx-images/hooks/useImageLightboxProvider';
import { ImageBlock, AspectRatio, ImageBlockCaptionPosition, Alignment } from '@lumapps/lumx/react';
import { resizeImage } from '@lumapps/medias/utils/resizeImage';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';
import { unescapeHTML } from '@lumapps/utils/string/unescapeHTML';
import { BlockComponent } from '@lumapps/widget-base/types';

import { BlockImagePreview as BlockImagePreviewProps } from '../../types';

import './index.scss';

const ASPECT_RATIO_MAP: Record<BlockImagePreviewProps['imageFill'], AspectRatio> = {
    original: AspectRatio.original,
    width: AspectRatio.horizontal,
    height: AspectRatio.vertical,
};

const CAPTION_POSITION_MAP: Record<BlockImagePreviewProps['captionPosition'], ImageBlockCaptionPosition> = {
    below: ImageBlockCaptionPosition.below,
    cover: ImageBlockCaptionPosition.over,
};

const CLASSNAME = 'block-image-preview';
export const BlockImagePreview: BlockComponent<BlockImagePreviewProps> = ({
    captionPosition,
    description,
    name,
    imageFill,
    image,
    theme,
}) => {
    // Unescape, so that & that have been converted to &amp; are displayed as &
    const sanitizedDescription = unescapeHTML(sanitizeHTML(description));

    const imageFullSize = useMemo(() => {
        return [
            {
                title: name,
                description: sanitizedDescription,
                alt: image.alt || '',
                image: resizeImage(image.url, MAX_IMAGE_SIZE),
            },
        ];
    }, [name, sanitizedDescription, image.alt, image.url]);

    const { getTriggerProps } = useImageLightboxProvider(imageFullSize);

    return (
        <ImageBlock
            className={CLASSNAME}
            captionPosition={CAPTION_POSITION_MAP[captionPosition]}
            description={sanitizedDescription}
            image={image.url}
            theme={theme}
            title={name}
            alt={image.alt || ''}
            align={Alignment.center}
            thumbnailProps={{
                aspectRatio: ASPECT_RATIO_MAP[imageFill],
                ...getTriggerProps?.(),
                style: { width: '100%' },
            }}
        />
    );
};
